import { GenerateMaintenanceScheduleTask, MoveDocumentTask, MoveTaskMetadata, Task } from '@/types/tasks';
import { TaskStatus } from '@/types/tasks.ts';
import { Tag } from '@/client';
import {
	CRITICAL_INFORMATION_TAG,
	NOT_A_FILE_TAGS,
	RecordTag,
	USER_DOCUMENT_LINK_TAG,
	USER_NOTE_TAG,
} from '@/constants/documentTags.ts';

export type Tags = Tag[] | string[] | undefined;

// Task status utilities
export const isTaskQueued = (task: Task): boolean => task.status === TaskStatus.Queued;
export const isTaskPending = (task: Task): boolean => task.status === TaskStatus.Pending;
export const isTaskInProgress = (task: Task): boolean => task.status === TaskStatus.InProgress;
export const isTaskCompleted = (task: Task): boolean => task.status === TaskStatus.Completed;
export const isTaskFailed = (task: Task): boolean => task.status === TaskStatus.Failed;

// Combined status utilities for UI purposes
export const isTaskActive = (task: Task): boolean =>
	task.status === TaskStatus.Pending || task.status === TaskStatus.InProgress;
export const isTaskActiveOrQueued = (task: Task): boolean => isTaskQueued(task) || isTaskActive(task);
export const isTaskFinished = (task: Task): boolean =>
	task.status === TaskStatus.Completed ||
	task.status === TaskStatus.Failed ||
	task.status === TaskStatus.Skipped ||
	task.status === TaskStatus.Cancelled;

// Task age utilities
export const getTaskAge = (task: Task): number => {
	if (!task.createdAt) return 0;
	return Date.now() - new Date(task.createdAt).getTime();
};

export const isTaskRecent = (task: Task, maxAgeMs: number = 5 * 60 * 1000): boolean => {
	return getTaskAge(task) <= maxAgeMs;
};

/**
 * Determines if a task should be persisted in localStorage
 * Based on task status and age - keeps active tasks and recent completed/failed tasks
 */
export const shouldPersistTask = (task: Task): boolean => {
	const taskAge = getTaskAge(task);

	return (
		// Always persist active tasks
		isTaskActive(task) ||
		// Keep recent completed tasks for 5 minutes
		(isTaskCompleted(task) && taskAge < 5 * 60 * 1000) ||
		// Keep recent failed tasks for 30 minutes
		(isTaskFailed(task) && taskAge < 30 * 60 * 1000)
	);
};

export const belongsToHouse = (task: Task, houseId: number): boolean => {
	return task.metadata?.houseId === houseId;
};

/** Safely reads the houseId from a task's loosely-typed metadata. */
export const getTaskHouseId = (task: Task): number | undefined => {
	const houseId = task.metadata?.houseId;
	return typeof houseId === 'number' ? houseId : undefined;
};

// Task type guards
const isMaintenanceTaskScheduleTask = (task: Task): task is GenerateMaintenanceScheduleTask => {
	return task.type === 'generate_maintenance_celery';
};

export const isMoveTask = (task: Task): task is MoveDocumentTask => {
	return (
		task.type === 'move' &&
		!!(task.metadata as MoveTaskMetadata)?.srcBucket &&
		!!(task.metadata as MoveTaskMetadata)?.dstBucket
	);
};

/**
 * Document type utilities for tasks based on metadata tags
 * These functions check if a task's metadata tags include specific document type tags
 */

/**
 * Checks if a task is for a note document
 */
export const isNoteTask = (task: Task): boolean => {
	return hasTagByName(task.metadata?.tags as Tags, USER_NOTE_TAG);
};

/**
 * Checks if a task is for a link document
 */
export const isLinkTask = (task: Task): boolean => {
	return hasTagByName(task.metadata?.tags as Tags, USER_DOCUMENT_LINK_TAG);
};

/**
 * Checks if a task is for an important document (important information)
 */
export const isImportantTask = (task: Task): boolean => {
	return hasTagByName(task.metadata?.tags as Tags, CRITICAL_INFORMATION_TAG);
};

/**
 * Checks if a task is for a regular file document (not a note, link, important, or contact)
 */
export const isFileTask = (task: Task): boolean => {
	return !NOT_A_FILE_TAGS.some((tagName) => hasTagByName(task.metadata?.tags as Tags, tagName));
};

// Tag utilities
export const hasTagByName = (tags: Tags, tagName: string): boolean => {
	if (!tags || !Array.isArray(tags)) return false;
	return tags.some((tag) => (typeof tag === 'string' ? tag === tagName : tag.name === tagName));
};

export const taskSelectors = {
	filterInProgress: (items: Task[]): Task[] =>
		items.filter((task): task is Task => isTaskInProgress(task) || isTaskPending(task)),
	filterCompleted: (items: Task[]): Task[] => items.filter(isTaskCompleted),
	filterFailed: (items: Task[]): Task[] => items.filter(isTaskFailed),
	filterIsGeneratingMaintenanceTask: (items: Task[]): Task[] => items.filter(isMaintenanceTaskScheduleTask),
	filterByTag: (items: Task[], tagName: string): Task[] =>
		items.filter((task) => hasTagByName(task.metadata?.tags as Tags, tagName)),
	filterWithoutTags: (items: Task[], tagNames: RecordTag[]): Task[] => {
		return items.filter((task) => {
			return !tagNames.some((tagName) => hasTagByName(task?.metadata?.tags as Tags, tagName));
		});
	},
	byHouse: (items: Task[], houseId: number): Task[] => items.filter((task) => belongsToHouse(task, houseId)),
};
